/** Every game moment a sound profile can give a sound to. Ids are stored in profiles: never rename one. */

export type SoundEventKind = 'sfx' | 'loop' | 'music';

export interface SoundEventDef {
  id: string;
  group: string;
  label: string;
  hint: string;
  kind: SoundEventKind;
}

const ev = (group: string, kind: SoundEventKind, rows: [id: string, label: string, hint: string][]): SoundEventDef[] =>
  rows.map(([id, label, hint]) => ({ id, group, label, hint, kind }));

export const SOUND_EVENTS: SoundEventDef[] = [
  ...ev('Player', 'sfx', [
    ['jump', 'Jump', 'Jumping off the ground'],
    ['wallJump', 'Wall jump', 'Kicking off a wall'],
    ['land', 'Land', 'Touching down after a fall'],
    ['footstep', 'Footstep', 'Each step while running'],
  ]),
  ...ev('Player', 'loop', [['wallSlide', 'Wall slide', 'Loops while sliding down a wall']]),
  ...ev('Player', 'sfx', [
    ['dash', 'Dash', 'Starting a dash'],
    ['dashReady', 'Dash recharged', 'The dash comes back after landing'],
    ['death', 'Death', 'Any death'],
    ['squish', 'Squished', 'Crushed between solid things (plays with Death)'],
    ['respawn', 'Respawn', 'Appearing at the spawn or checkpoint'],
  ]),
  ...ev('Player', 'loop', [['pushBlock', 'Push or drag block', 'Loops while moving a block']]),
  ...ev('Player', 'sfx', [['playerJoin', 'Player joins', 'Another player drops into the level']]),

  ...ev('Power-ups', 'sfx', [['powerup', 'Power-up pickup', 'Grabbing any power-up']]),
  ...ev('Power-ups', 'loop', [['jetpack', 'Jetpack thrust', 'Loops while the jetpack fires']]),
  ...ev('Power-ups', 'sfx', [
    ['jetpackEmpty', 'Jetpack empty', 'Fuel runs out'],
    ['bigDude', 'Big Dude', 'Growing big'],
    ['blockDude', 'Block Dude', 'Turning into a block'],
    ['doubleDash', 'Double dash', 'Getting the double dash'],
    ['powerupEnd', 'Power-up ends', 'A power-up runs out or is lost'],
  ]),

  ...ev('Pickups', 'sfx', [
    ['coin', 'Coin', 'Collecting a coin'],
    ['gem', 'Gem', 'Collecting a gem'],
    ['gemShard', 'Gem shard', 'Collecting one shard'],
    ['shardsComplete', 'All shards', 'The last shard of a group'],
    ['reDash', 'Dash refill', 'Touching a dash refill orb'],
    ['rePack', 'Jetpack refill', 'Touching a jetpack refill'],
    ['forceDash', 'Force dash', 'A force-dash orb launches you'],
  ]),

  ...ev('Level', 'sfx', [
    ['checkpoint', 'Checkpoint', 'A checkpoint saves'],
    ['doorOpen', 'Door opens', 'The first player steps into an exit door'],
    ['doorClose', 'Door closes', 'The last player steps back out of a door'],
    ['doorEnter', 'Enter door', 'Another player joins them in the door'],
    ['levelStart', 'Level start', 'Play begins'],
    ['levelComplete', 'Level complete', 'The level is finished'],
    ['restart', 'Restart', 'The level restarts'],
  ]),

  ...ev('Hazards', 'sfx', [
    ['cannon', 'Cannon', 'A cannon fires'],
    ['plasmaShot', 'Plasma shot', 'A plasma emitter fires'],
    ['projectileHit', 'Projectile hit', 'A shot hits something'],
    ['explosion', 'Explosion', 'Something blows up'],
    ['laser', 'Laser on', 'A laser switches on'],
    ['spikeyBall', 'Spikey ball pops', 'A spikey ball is destroyed'],
    ['patroller', 'Patroller hop', 'A spikey patroller turns or hops'],
    ['launcher', 'Launcher', 'A launcher fires'],
  ]),

  ...ev('Platforms & switches', 'sfx', [
    ['crumble', 'Crumble starts', 'A crumble platform starts shaking'],
    ['crumbleBreak', 'Crumble breaks', 'A crumble platform falls apart'],
    ['platformRespawn', 'Platform returns', 'A crumbled or faded platform comes back'],
    ['touchBlock', 'Touch block', 'A touch block lights up'],
    ['tickTock', 'Tick-tock platform', 'A tick-tock platform flips'],
    ['dashPlatform', 'Dash platform', 'A dash platform turns solid or clear'],
    ['colorBlock', 'Color block', 'A color block turns solid or clear'],
    ['fadeOut', 'Fade platform', 'A fade platform starts fading'],
    ['breakTile', 'Breakable tile', 'A breakable tile shatters'],
    ['switchOn', 'Switch on', 'A switch or zone turns on'],
    ['switchOff', 'Switch off', 'A switch or zone turns off'],
    ['timerTick', 'Timer tick', 'Each tick of a timed activator'],
    ['platformMove', 'Moving platform', 'A moving platform sets off'],
    ['rotator', 'Rotator', 'A rotator starts turning'],
    ['controlPanel', 'Control panel on', 'Taking control at a panel'],
    ['controlPanelOff', 'Control panel off', 'Letting go of a panel'],
    ['stringTie', 'String', 'The string ties or unties players'],
  ]),

  ...ev('Game modes', 'sfx', [
    ['kothPoint', 'King of the hill life lost', 'A player loses one of their lives'],
    ['kothOut', 'King of the hill out', 'A player loses their last life'],
    ['volleyHit', 'Volleyball hit', 'Someone hits the ball'],
    ['volleyScore', 'Volleyball score', 'The ball lands for a point'],
    ['winner', 'Winner', 'One player is left standing in king of the hill'],
  ]),

  ...ev('Menus', 'sfx', [
    ['uiClick', 'Click', 'Pressing a menu button'],
    ['uiMove', 'Move', 'Moving between menu buttons'],
    ['uiConfirm', 'Confirm', 'Confirming a choice'],
    ['uiBack', 'Back', 'Backing out of a menu'],
    ['uiPause', 'Pause', 'Opening the pause menu'],
    ['uiResume', 'Resume', 'Closing the pause menu'],
    ['uiError', 'Error', 'Pause is refused, e.g. mid-point in volleyball'],
  ]),

  ...ev('Music', 'music', [
    ['musicLevel', 'Level music', 'Plays while you play a level alone'],
    ['musicMultiplayer', 'Multiplayer music', 'Plays when two or more players are in'],
    ['musicMenu', 'Menu music', 'Plays in the lobby before a game'],
    ['musicVictory', 'Victory music', 'Plays after the level is complete'],
  ]),
];

export const SOUND_EVENT_BY_ID = new Map(SOUND_EVENTS.map((e) => [e.id, e]));

export const SOUND_GROUPS = [...new Set(SOUND_EVENTS.map((e) => e.group))];

/** Unity SoundType names the sim and level data still use */
export const LEGACY_SOUND_NAMES: Record<string, string> = {
  Jump: 'jump',
  WallJump: 'wallJump',
  Coin: 'coin',
  Dash: 'dash',
  Jetpack: 'jetpack',
  Checkpoint: 'checkpoint',
  PlayerDeath: 'death',
  UIButton: 'uiClick',
  Powerup: 'powerup',
  Cannon: 'cannon',
};

export function soundEventId(name: string): string {
  return LEGACY_SOUND_NAMES[name] ?? name;
}
